// ─── Hero: Album Sleeve ───────────────────────────────────────
// Dark vinyl-inspired hero with framed photo, rotating vinyl disc,
// equalizer bars, and song title overlay. Matches NoirVinyl exactly.

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { StaggerContainer } from '../../../lib/animations/AnimatedSection';

export default function HeroAlbumSleeve({ section, data, theme, settings }) {
  const {
    title, dateText, subtitle, backgroundImage,
    songTitle, artistName, vinylCoverImage, vinylColor,
  } = data;
  const anim = section.animation || {};

  const audioRef = useRef(null);
  const fadeRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const hasAutoPlayed = useRef(false);

  const musicUrl = data.musicUrl || settings?.musicUrl || '';

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPause = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('play', onPlay);
    return () => {
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('play', onPlay);
      if (fadeRef.current) clearInterval(fadeRef.current);
    };
  }, []);

  useEffect(() => {
    if (!musicUrl) return;
    const autoStart = () => {
      if (hasAutoPlayed.current) return;
      hasAutoPlayed.current = true;
      const audio = audioRef.current;
      if (!audio || !audio.paused) return;
      audio.volume = 0;
      audio.play().then(() => {
        let vol = 0;
        fadeRef.current = setInterval(() => {
          vol = Math.min(vol + 0.04, 1);
          audio.volume = vol;
          if (vol >= 1) clearInterval(fadeRef.current);
        }, 100);
      }).catch(() => {});
      document.removeEventListener('click', autoStart);
      document.removeEventListener('scroll', autoStart);
    };
    document.addEventListener('click', autoStart, { once: true });
    document.addEventListener('scroll', autoStart, { once: true });
    return () => {
      document.removeEventListener('click', autoStart);
      document.removeEventListener('scroll', autoStart);
    };
  }, [musicUrl]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (audio.paused) {
        audio.volume = 0;
        await audio.play();
        let vol = 0;
        fadeRef.current = setInterval(() => {
          vol = Math.min(vol + 0.05, 1);
          audio.volume = vol;
          if (vol >= 1) clearInterval(fadeRef.current);
        }, 80);
      } else {
        audio.pause();
      }
    } catch (e) {}
  }, []);

  const eqBars = [4, 8, 6, 12, 9, 14, 7, 11, 5, 13, 8, 10, 6];
  const rgba = (hex, a) => {
    const h = (hex || '#ffffff').replace('#', '');
    const r = parseInt(h.substring(0, 2), 16) || 255;
    const g = parseInt(h.substring(2, 4), 16) || 255;
    const b = parseInt(h.substring(4, 6), 16) || 255;
    return `rgba(${r},${g},${b},${a})`;
  };

  return (
    <section className="relative w-full overflow-hidden" style={{ minHeight: 480, backgroundColor: theme.bg }} data-testid="hero-section">
      {musicUrl && <audio ref={audioRef} src={musicUrl} loop preload="none" />}

      <style>{`
        @keyframes eqBounce {
          0% { transform: scaleY(0.25); }
          25% { transform: scaleY(0.9); }
          50% { transform: scaleY(0.4); }
          75% { transform: scaleY(1); }
          100% { transform: scaleY(0.25); }
        }
      `}</style>

      {/* Background image with blur */}
      {backgroundImage && (
        <>
          <img
            src={backgroundImage}
            alt=""
            draggable={false}
            className="absolute"
            style={{
              top: '-12px', left: '-12px',
              width: 'calc(100% + 24px)', height: 'calc(100% + 24px)',
              objectFit: 'cover', zIndex: 0,
              filter: 'blur(5px)',
            }}
          />
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.1)', zIndex: 1 }} />
        </>
      )}

      <div className="relative z-10 flex flex-col items-center px-6 pt-10 pb-8">
        {/* Album frame with photo */}
        <div className="relative" style={{ width: 320, margin: '0 auto' }}>
          <div
            className="relative overflow-hidden"
            style={{ aspectRatio: '1/1', border: '3px solid rgba(255,255,255,0.2)' }}
          >
            {backgroundImage ? (
              <img
                src={backgroundImage}
                alt={title}
                className="w-full h-full object-cover"
                style={{ filter: 'grayscale(100%) contrast(1.1)' }}
                draggable={false}
              />
            ) : (
              <div className="w-full h-full bg-neutral-900 flex items-center justify-center">
                <p className="text-sm" style={{ color: rgba(theme.textPrimary, 0.2) }}>Upload a photo</p>
              </div>
            )}

            {/* Equalizer bars */}
            <div className="absolute bottom-3 left-3 flex items-end gap-[2px] z-20">
              {eqBars.map((h, i) => (
                <div
                  key={i}
                  className="rounded-sm origin-bottom"
                  style={{
                    width: '3px', height: `${h}px`,
                    backgroundColor: rgba(theme.textPrimary, 0.6),
                    ...(isPlaying ? {
                      animation: `eqBounce ${0.6 + (i % 5) * 0.2}s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
                      animationDelay: `${i * 0.08}s`,
                    } : { transform: 'scaleY(0.3)', transition: 'transform 0.4s ease' }),
                  }}
                />
              ))}
            </div>

            {/* Song title overlay */}
            {(songTitle || artistName) && (
              <div className="absolute bottom-0 left-0 right-0 z-20 px-4 pb-4 pt-12 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                <p className="text-lg font-light tracking-wide" style={{ color: theme.textPrimary, fontFamily: theme.headingFont }}>
                  {songTitle || 'Our Song'}
                </p>
                <p className="text-xs mt-0.5" style={{ color: rgba(theme.textPrimary, 0.6) }}>
                  {artistName || 'Artist'}
                </p>
              </div>
            )}
          </div>

          {/* Vinyl disc */}
          {musicUrl && (
            <div
              className="absolute z-20 cursor-pointer group"
              style={{ top: '5%', right: '-15px', width: '130px' }}
              onClick={togglePlay}
              data-testid="vinyl-disc"
            >
              <div className="relative" style={{ paddingBottom: '100%' }}>
                <motion.div
                  animate={{ rotate: isPlaying ? 360 : 0 }}
                  transition={{ duration: 4, ease: 'linear', repeat: isPlaying ? Infinity : 0 }}
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: vinylColor
                      ? `radial-gradient(circle at center, ${vinylColor} 0%, ${vinylColor}cc 50%, ${vinylColor}99 70%, ${vinylColor}66 100%)`
                      : 'radial-gradient(circle at center, #2f2f2f 0%, #171717 50%, #0d0d0d 70%, #000 100%)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
                    border: '2px solid rgba(255,255,255,0.15)',
                  }}
                >
                  {[10, 18, 26, 34].map((inset) => (
                    <div key={inset} className="absolute rounded-full border border-white/[0.08]" style={{ inset: `${inset}%` }} />
                  ))}
                  <div
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full overflow-hidden border border-white/20"
                    style={{ width: '52%', height: '52%' }}
                  >
                    {(vinylCoverImage || backgroundImage) ? (
                      <img src={vinylCoverImage || backgroundImage} alt="" className="w-full h-full object-cover" style={{ filter: 'grayscale(100%)' }} />
                    ) : (
                      <div className="w-full h-full bg-neutral-700" />
                    )}
                  </div>
                  <div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black border border-white/30 z-10" />
                </motion.div>
              </div>
            </div>
          )}
        </div>

        {/* Text content below frame */}
        <StaggerContainer preset={anim.title || 'stagger-text'} className="mt-8 text-center">
          <h1
            className="text-3xl sm:text-4xl font-light"
            style={{ color: theme.textPrimary, fontFamily: theme.headingFont, textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
          >
            {title || 'Our Wedding'}
          </h1>
          {dateText && (
            <p className="text-sm mt-3 tracking-wider" style={{ color: rgba(theme.textPrimary, 0.6), fontFamily: theme.eyebrowFont }}>
              {dateText}
            </p>
          )}
          {subtitle && (
            <p className="text-xs mt-1.5 tracking-wider" style={{ color: rgba(theme.textPrimary, 0.4), fontFamily: theme.eyebrowFont }}>
              {subtitle}
            </p>
          )}
        </StaggerContainer>
      </div>
    </section>
  );
}
